import {
  addEdge,
  Connection,
  Edge,
  EdgeProps,
  getOutgoers,
  IsValidConnection,
  Node,
  useEdgesState,
  useNodesState,
  useReactFlow,
} from '@xyflow/react';
import { useAtomValue } from 'jotai';
import { DragEventHandler, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CommonFlowCard, CustomEdge } from '@/lib/v3/components/Flows';

import { atomDragType } from '@/src/modules/AutomationModuleNew/atoms/flows';
import {
  SOURCE_HANDLE_NO,
  SOURCE_HANDLE_YES,
} from '@/src/modules/AutomationModuleNew/constants/flows';
import ConditionalNode, {
  ConditionalNodeProps,
} from '@/src/modules/AutomationModuleNew/screens/JourneysScreen/components/ConditionalNodes/ConditionalNode';
import { TriggerNode } from '@/src/modules/AutomationModuleNew/screens/JourneysScreen/components/TriggerNodes';
import {
  getInitialNodeData,
  getMainNodeType,
  getNextEdgeId,
  getNextNodeId,
} from '@/src/modules/AutomationModuleNew/utils/journeysHelper';

import ActionNode, {
  ActionNodeProps,
} from '@/modules/AutomationModuleNew/screens/JourneysScreen/components/ActionNodes/ActionNode';

interface UseManageHandleFlowProps {
  initialNodes: Node[];
  initialEdges: Edge[];
}

export const useManageHandleFlow = ({ initialEdges, initialNodes }: UseManageHandleFlowProps) => {
  const { t } = useTranslation();
  const [nodes, setNodes, onNodesChange] = useNodesState(initialNodes);
  const [edges, setEdges, onEdgesChange] = useEdgesState(initialEdges);
  const { screenToFlowPosition, deleteElements } = useReactFlow();

  const handleDeleteNode = useCallback(
    async (nodeId: number | string) => {
      await deleteElements({ nodes: [{ id: nodeId.toString() }] });
    },
    [deleteElements]
  );

  const nodeTypes = useMemo(
    () => ({
      trigger: TriggerNode,
      action: (props: ActionNodeProps) => <ActionNode {...props} onDelete={handleDeleteNode} />,
      conditional: (props: ConditionalNodeProps) => (
        <ConditionalNode {...props} onDelete={handleDeleteNode} />
      ),
      unknown: (props: ActionNodeProps) => (
        <CommonFlowCard
          {...props}
          borderColor="default"
          id={props.id}
          info={t('ActionNode.removeNode')}
          title={t('ActionNode.default')}
          onDelete={handleDeleteNode}
        />
      ),
    }),
    [handleDeleteNode, t]
  );

  const handleDeleteEdge = useCallback(
    async (edgeId: number | string) => {
      await deleteElements({ edges: [{ id: edgeId.toString() }] });
    },
    [deleteElements]
  );

  const edgeTypes = useMemo(
    () => ({
      customEdge: (props: EdgeProps) => <CustomEdge {...props} onDelete={handleDeleteEdge} />,
    }),
    [handleDeleteEdge]
  );

  const dragType = useAtomValue(atomDragType);

  const isValidConnection: IsValidConnection<Edge> = useCallback(
    (connection) => {
      const sourceHasConnections = edges.some((edge) => {
        if (connection.sourceHandle === 'yes' || connection.sourceHandle === 'no') {
          return edge.source === connection.source && edge.sourceHandle === connection.sourceHandle;
        }
        return edge.source === connection.source;
      });

      if (sourceHasConnections) return false;

      const target = nodes.find((node) => node.id === connection.target);

      const hasCycle = (node: Node, visited = new Set()) => {
        if (visited.has(node.id)) return false;

        visited.add(node.id);

        for (const outgoer of getOutgoers(node, nodes, edges)) {
          if (outgoer.id === connection.source) return true;
          if (hasCycle(outgoer, visited)) return true;
        }
      };

      if (!target) return false;
      if (target.id === connection.source) return false;

      return !hasCycle(target);
    },
    [edges, nodes]
  );

  const handleConnect = useCallback(
    (params: Connection) => {
      const createEdge = () => {
        const { sourceHandle } = params;
        let label: string | undefined;

        if (sourceHandle === SOURCE_HANDLE_YES) {
          label = t('flows.yes');
        } else if (sourceHandle === SOURCE_HANDLE_NO) {
          label = t('flows.no');
        }

        setEdges((eds) =>
          addEdge(
            {
              ...params,
              id: getNextEdgeId(eds),
              type: 'customEdge',
              label,
            },
            eds
          )
        );
      };

      return createEdge();
    },
    [setEdges, t]
  );

  const handleDragOver: DragEventHandler<HTMLDivElement> = useCallback((event) => {
    event.preventDefault();
    event.dataTransfer.dropEffect = 'move';
  }, []);

  const handleDrop: DragEventHandler<HTMLDivElement> = useCallback(
    (event) => {
      event.preventDefault();

      if (!dragType) {
        return;
      }

      const position = screenToFlowPosition({
        x: event.clientX,
        y: event.clientY,
      });

      const initialData = getInitialNodeData(dragType);
      const nodeId = getNextNodeId();

      const newNode: Node = {
        id: nodeId,
        type: getMainNodeType(dragType),
        position,
        data: {
          ...initialData,
          id: nodeId,
        },
      };

      setNodes((nds) => nds.concat(newNode));
    },
    [dragType, screenToFlowPosition, setNodes]
  );

  return {
    nodes,
    edges,
    nodeTypes,
    edgeTypes,
    onNodesChange,
    onEdgesChange,
    handleConnect,
    handleDragOver,
    handleDrop,
    isValidConnection,
  };
};
